import { createContext, useContext, useEffect, useState } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import type { UserRole, UserProfile } from '../types/auth'

interface AuthContextType {
  user: User | null
  session: Session | null
  profile: UserProfile | null
  role: UserRole | null
  loading: boolean
  signOut: () => Promise<void>
  isAdmin: boolean
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  profile: null,
  role: null,
  loading: true,
  signOut: async () => {},
  isAdmin: false,
})

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    console.log('🔐 AuthProvider: Initializing...')

    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session }, error }) => {
      console.log('📊 Session check:', {
        hasSession: !!session,
        error: error?.message || null,
        url: window.location.href,
        hash: window.location.hash
      })

      if (error) {
        console.error('❌ Session error:', error)
      }

      setSession(session)
      setUser(session?.user ?? null)

      if (session?.user) {
        console.log('👤 User found:', session.user.email)
        console.log('👤 User ID:', session.user.id)
        await fetchProfile(session.user.id)
      } else {
        console.log('❌ No session found')
        console.log('   URL hash:', window.location.hash)
        console.log('   URL search:', window.location.search)
      }

      setLoading(false)
    })

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔄 Auth state changed:', event, !!session)

      setSession(session)
      setUser(session?.user ?? null)

      if (session?.user) {
        console.log('✅ Session created for:', session.user.email)
        await fetchProfile(session.user.id)
      } else {
        setProfile(null)
      }

      setLoading(false)
    })

    return () => subscription.unsubscribe()
  }, [])

  const fetchProfile = async (userId: string) => {
    try {
      console.log('🔍 Fetching profile for user:', userId)
      console.log('   Calling supabase.auth.getUser()...')

      const result = await Promise.race([
        supabase.auth.getUser(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout getting user')), 5000))
      ])

      const userData = (result as { data: { user?: { email?: string } } }).data

      const userEmail = userData.user?.email
      console.log('📧 User email:', userEmail)

      // Check if user is admin based on email
      const ADMIN_EMAILS = ['cyartluthier@gmail.com', 'alexandre@binaree.io']
      const isAdminEmail = userEmail && ADMIN_EMAILS.includes(userEmail.toLowerCase())
      console.log('🔑 Is admin email?', isAdminEmail)

      console.log('🔍 Querying profiles table...')

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single()

      console.log('📥 Profile query result:', { data: !!data, error: error?.message })

      if (error) {
        console.log('⚠️ Profile not found, creating new one...', error)
        // If profile doesn't exist, create a new profile with appropriate role
        const newProfile: Partial<UserProfile> = {
          id: userId,
          role: isAdminEmail ? 'admin' : 'client',
          email: userEmail || '',
        }

        const { data: createdProfile, error: insertError } = await supabase
          .from('profiles')
          .insert([newProfile])
          .select()
          .single()

        if (insertError) {
          console.error('❌ Error creating profile:', insertError)
        } else {
          console.log('✅ Profile created:', createdProfile)
          setProfile(createdProfile)
        }
      } else {
        console.log('✅ Profile found:', data)
        setProfile(data)
      }
    } catch (error) {
      console.error('❌ Error in fetchProfile:', error)
    }
  }

  const signOut = async () => {
    await supabase.auth.signOut()
  }

  const value = {
    user,
    session,
    profile,
    role: profile?.role ?? null,
    loading,
    signOut,
    isAdmin: profile?.role === 'admin',
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
